import { Close } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import { ListUserEmailSettingRes, UseCaseType } from "src/entity/recon-entity/ReconInterfaces";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { exists } from "src/Utils/Common/Validators";
import BcNewContext from "../AuditCommunication/Components/BcNewContext";
import { StateDispatch } from "../CommonLegacy/CommonComponents";
import { PartnerCommsContext } from "../Context/PartnerCommunicationContext";
import { NdAsyncButton, NdButton } from "../MsmePartnerPortal/CommonComponents";
import $ from "../MsmePartnerPortal/PartnerPortal.module.scss";

export interface CommonDialogProps {
  open: boolean;
  setOpen: StateDispatch<boolean>;
}

interface ApproveResponseDialogProps extends CommonDialogProps {
  partnerCount: number;
  approveAction: () => Promise<void>;
}
interface RejectResponseDialogProps extends CommonDialogProps {
  rejectAction: (rejectReason: string) => Promise<void>;
}

export const ApproveResponseDialog = ({ open, setOpen, partnerCount, approveAction }: ApproveResponseDialogProps) => {
  const [waiting, setWaiting] = React.useState(false);
  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: $.BR_fix }} maxWidth="sm" fullWidth>
        <DialogTitle>Have you reviewed submitted response of {partnerCount ? partnerCount : ""} partners?</DialogTitle>
        <DialogContent className={$.flex_column} sx={{ py: 2, px: 3 }}>
          <Alert severity="info">
            Response submitted will be freezed and can't be edited. Make sure you have reviewed the partner's response.
          </Alert>
        </DialogContent>
        <DialogActions>
          <NdAsyncButton
            className={$.BR_fix}
            onClick={async () => {
              setWaiting(true);
              await approveAction();
              setOpen(false);
              setWaiting(false);
            }}
            color="primary"
            buttonComponent={Button}
          >
            Yes
          </NdAsyncButton>
          <Button
            className={$.BR_fix}
            onClick={() => {
              setOpen(false);
            }}
            color="primary"
            disabled={waiting}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const RejectResponseDialog = ({ open, setOpen, rejectAction }: RejectResponseDialogProps) => {
  const [waiting, setWaiting] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState("");
  const [selectedReasons, setSelectedReasons] = React.useState<string[]>([]);
  const [isOthersEnabled, setIsOthersEnabled] = React.useState(false);

  // Dynamic rejection reasons list
  const rejectionReasons = [
    "Please submit the ledger in Excel format",
    "Please share the PAN-level ledger",
    "Please share the complete ledger with all entities",
    "Please share the ledger from inception",
  ];

  const handleReasonChange = (reason: string, checked: boolean) => {
    if (checked) {
      setSelectedReasons((prev) => [...prev, reason]);
    } else {
      setSelectedReasons((prev) => prev.filter((r) => r !== reason));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setRejectReason("");
    setSelectedReasons([]);
    setIsOthersEnabled(false);
  };
  return (
    <>
      <Dialog open={open} maxWidth={DIALOG_MAX_WIDTHS.MD} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m error-dialog-icon">
            <div>
              <WarningAmberIcon color="error" />
            </div>
            Reject Response
          </div>
          <div className="vertical_center_align">
            <IconButton
              onClick={() => {
                handleClose();
              }}
            >
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {/* reject reason checkboxes */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {rejectionReasons.map((reason, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedReasons.includes(reason)}
                    onChange={(e) => handleReasonChange(reason, e.target.checked)}
                  />
                }
                label={reason}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "16px",
                    color: "rgba(0,0,0,0.87)",
                    fontFamily: "Roboto, sans-serif",
                    lineHeight: 1.5,
                  },
                }}
              />
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isOthersEnabled}
                  onChange={(e) => {
                    setIsOthersEnabled(e.target.checked);
                    if (!e.target.checked) {
                      setRejectReason("");
                    }
                  }}
                />
              }
              label={
                <TextField
                  className="update-custom-border-radius"
                  placeholder="Write the rejection reason."
                  variant="outlined"
                  fullWidth
                  multiline
                  maxRows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  label="Others"
                  size="small"
                  disabled={!isOthersEnabled}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: "400px",
                    mt: 0.5,
                    "& .MuiInputBase-root": {
                      fontSize: "16px",
                      fontFamily: "Roboto, sans-serif",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "12px",
                      fontFamily: "Roboto, sans-serif",
                    },
                  }}
                />
              }
              sx={{
                alignItems: "flex-start",
                width: "100%",
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions className="dialog-actions">
          <CustomButton
            onClick={() => {
              handleClose();
            }}
            size="medium"
            color="error"
            variant="text"
          >
            Cancel
          </CustomButton>
          <CustomButton
            size="medium"
            color="error"
            variant="contained"
            onClick={async () => {
              setWaiting(true);
              const allReasons = [...selectedReasons];
              if (rejectReason.trim()) {
                allReasons.push(rejectReason.trim());
              }
              await rejectAction(allReasons.join(", "));
              handleClose();
              setWaiting(false);
            }}
            disabled={waiting || (selectedReasons.length === 0 && !rejectReason.trim())}
          >
            <LoadingIcon loading={waiting} />
            Reject
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ResetStatusDialogProps extends CommonDialogProps {
  partnerCount: number;
  partnerName: string;
  resetAction: () => Promise<void>;
}

export const ResetStatusDialog = ({
  open,
  setOpen,
  partnerCount,
  partnerName,
  resetAction,
}: ResetStatusDialogProps) => {
  const [waiting, setWaiting] = React.useState(false);

  return (
    <>
      <Dialog open={open} classes={{ paper: $.BR_fix }} maxWidth="md" fullWidth>
        <DialogTitle textTransform={"uppercase"} className="textOverflow_hidden d_flex">
          Have you reviewed{"  "}
          <h2
            style={{ font: "inherit", maxWidth: 400, display: "inline-block", paddingLeft: 6 }}
            className="textOverflow_hidden"
          >
            {partnerName ? partnerName : `all of ${partnerCount} partner`}
          </h2>
          's response?
        </DialogTitle>
        <DialogContent className={$.flex_column} sx={{ py: 2, px: 3 }}>
          <Alert severity="warning" sx={{ "*": { fontFamily: "inherit" } }}>
            This action will reset the communication status of the selected partner and cannot be reverted
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            className={$.BR_fix}
            onClick={() => {
              setOpen(false);
            }}
            color="primary"
            disabled={waiting}
          >
            Cancel
          </Button>
          <NdAsyncButton
            variant="contained"
            className={$.BR_fix}
            onClick={async () => {
              setWaiting(true);
              await resetAction();
              setOpen(false);
              setWaiting(false);
            }}
            color="warning"
            buttonComponent={Button}
          >
            Reset
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface UpdateEmailTemplateDialogProps extends CommonDialogProps {
  noAction?: () => Promise<void>;
  yesAction?: () => Promise<void>;
}

export const UpdateEmailTemplateDialog = ({ open, setOpen, yesAction, noAction }: UpdateEmailTemplateDialogProps) => {
  const [waiting, setWaiting] = React.useState(false);
  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(waiting ? true : false)}
        classes={{ paper: $.BR_fix }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update E-mail template</DialogTitle>
        <DialogContent className={$.flex_column} sx={{ py: 2, px: 3 }}>
          <Alert severity="warning">Do you want to update the email template to use it in future?</Alert>
        </DialogContent>
        <DialogActions>
          <NdAsyncButton
            className={$.BR_fix}
            variant="text"
            buttonComponent={Button}
            color="error"
            onClick={async () => {
              setWaiting(true);
              await noAction();
              setOpen(false);
              setWaiting(false);
            }}
            disabled={waiting}
          >
            No
          </NdAsyncButton>
          <NdAsyncButton
            className={$.BR_fix}
            variant="contained"
            onClick={async () => {
              setWaiting(true);
              await yesAction();
              setOpen(false);
              setWaiting(false);
            }}
            color="primary"
          >
            Yes
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const UpdateUserEmailSettingDialog = ({ open, setOpen }: CommonDialogProps) => {
  const { actor } = useContext(userContext);
  const { setUserEmailSetting } = useContext(PartnerCommsContext);

  const [waiting, setWaiting] = React.useState(false);

  // User Display Name and Email Id
  const getListUserEmailSetting = async () => {
    return useFetch<ListUserEmailSettingRes>(API_ENDPOINTS.LIST_USER_EMAIL_SETTING.url, "GET", {
      failureMessage: API_ENDPOINTS.LIST_USER_EMAIL_SETTING.failureMessage,
      config: {
        params: {
          userId: actor.userId,
        },
      },
      thenCallBack: (response) => {
        if (exists(response.data.userEmailSetting)) {
          setUserEmailSetting(response.data.userEmailSetting);
        }
      },
    });
  };

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: $.BR_fix }} maxWidth="sm" fullWidth>
        <DialogTitle>Update User Email Settings?</DialogTitle>
        <DialogContent className={$.flex_column} sx={{ py: 2, px: 3 }}>
          <Alert severity="info" sx={{ "& *": { fontFamily: "inherit" } }}>
            Please update to reflect changes in email settings
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            className={$.BR_fix}
            onClick={() => {
              setOpen(false);
            }}
            color="inherit"
            disabled={waiting}
          >
            No
          </Button>
          <NdAsyncButton
            className={$.BR_fix}
            variant="contained"
            onClick={async () => {
              setWaiting(true);
              await getListUserEmailSetting();
              setOpen(false);
              setWaiting(false);
            }}
            // buttonComponent={Button}
          >
            Yes
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface MailStatusRefreshDialogProps extends CommonDialogProps {
  approveAction: () => Promise<void>;
}

export const MailStatusRefreshDialog = ({ open, setOpen, approveAction }: MailStatusRefreshDialogProps) => {
  const [waiting, setWaiting] = React.useState(false);
  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: $.BR_fix }} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status!</DialogTitle>
        <DialogContent className={$.flex_column} sx={{ py: 2, px: 3 }}>
          <Alert severity="info">
            You have not refreshed partner replies since some time, do you still want to proceed?
          </Alert>
        </DialogContent>
        <DialogActions>
          <NdAsyncButton
            className={$.BR_fix}
            onClick={async () => {
              setWaiting(true);
              await approveAction();
              setOpen(false);
              setWaiting(false);
            }}
            color="primary"
            buttonComponent={Button}
          >
            Proceed
          </NdAsyncButton>
          <Button
            className={$.BR_fix}
            onClick={() => {
              setOpen(false);
            }}
            color="primary"
            disabled={waiting}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface StopAutoReminderProps extends CommonDialogProps {
  stopAction: () => Promise<void>;
  partnerName?: string;
}

export const StopAutoReminder = ({ open, setOpen, stopAction, partnerName }: StopAutoReminderProps) => {
  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: $.BR_fix }} maxWidth="sm" fullWidth>
        <DialogTitle>Are you sure you want to stop auto-reminder?</DialogTitle>
        <DialogContent className={$.flex_column} sx={{ py: 2, px: 3 }}>
          <Alert severity="warning" sx={{ "& *": { fontFamily: "inherit" } }}>
            This auto-reminder will be stopped and cannot be reinstated {partnerName ? "For the Partner " : ""}
            {partnerName ? <b>{partnerName}</b> : ""}.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            className={$.BR_fix}
            onClick={() => {
              setOpen(false);
            }}
            color="inherit"
          >
            No
          </Button>
          <NdAsyncButton
            className={$.BR_fix}
            variant="contained"
            buttonComponent={Button}
            color="error"
            onClick={async () => {
              await stopAction();
              setOpen(false);
            }}
          >
            STOP AUTO REMINDER
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface SaveTableViewProps extends CommonDialogProps {
  initialName?: string;
  updateAction: (name: string) => Promise<void>;
  saveAction: (name: string) => Promise<void>;
}

export const SaveTableView = ({ open, setOpen, initialName, updateAction, saveAction }: SaveTableViewProps) => {
  const [name, setName] = useState(initialName);
  const [waiting, setWaiting] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
    setName("");
  };

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  return (
    <>
      <Dialog open={open} onClose={() => handleClose()} classes={{ paper: $.BR_fix }} maxWidth="xs" fullWidth>
        <Box className="dialog_header space_between" alignItems="center" pr={1}>
          <DialogTitle component={"div"} width={"90%"}>
            Save Table View
          </DialogTitle>
          <IconButton
            onClick={() => {
              handleClose();
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ display: "flex", flexFlow: "column", gap: 1 }}>
          <TextField
            className={$.BR_fix}
            label="Enter table view name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            size="medium"
            sx={{ ".MuiInputBase-root": { borderRadius: "4px" } }}
          />
        </DialogContent>
        <DialogActions className="dialog_footer">
          {initialName && (
            <NdAsyncButton
              className={$.BR_fix}
              variant="contained"
              disabled={waiting}
              onClick={async () => {
                setWaiting(true);
                await updateAction(name);
                setWaiting(false);
                setOpen(false);
              }}
            >
              Update Existing
            </NdAsyncButton>
          )}
          <NdAsyncButton
            className={$.BR_fix}
            variant="contained"
            disabled={waiting}
            onClick={async () => {
              if (!name.trim()) return; // Add validation
              setWaiting(true);
              await saveAction(name);
              setWaiting(false);
              setOpen(false);
            }}
          >
            Save as New
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const DeletePDFTemplate = ({
  open,
  setOpen,
  templateId,
  listUserEmailTemplates,
}: CommonDialogProps & { listUserEmailTemplates: () => void; templateId: number }) => {
  const [waiting, setWaiting] = React.useState(false);

  // Delete PDF Template
  const deletePDFTemplate = (_templateId: number) => {
    return useFetch(API_ENDPOINTS.DELETE_PC_PDF_TEMPLATE.url, "POST", {
      failureMessage: API_ENDPOINTS.DELETE_PC_PDF_TEMPLATE.failureMessage,
      showSuccessToast: true,
      data: {
        templateId: _templateId,
        templateType: UseCaseType.balanceConfirmation,
      },
      thenCallBack: (_res) => {
        listUserEmailTemplates();
      },
    });
  };

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: $.BR_fix }} maxWidth="sm" fullWidth>
        <DialogTitle>Delete PDF Template?</DialogTitle>
        <DialogContent className={$.flex_column} sx={{ py: 2, px: 3 }}>
          <Alert severity="warning" sx={{ "& *": { fontFamily: "inherit" } }}>
            The PDF Template will be Deleted!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            className={$.BR_fix}
            onClick={() => {
              setOpen(false);
            }}
            color="inherit"
            disabled={waiting}
          >
            No
          </Button>
          <NdAsyncButton
            className={$.BR_fix}
            variant="contained"
            onClick={async () => {
              setWaiting(true);
              await deletePDFTemplate(templateId);
              setOpen(false);
              setWaiting(false);
            }}
            // buttonComponent={Button}
          >
            Yes
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface DeleteTableViewProps extends CommonDialogProps {
  initialName: string;
  delAction: () => Promise<void>;
}

export const DeleteTableView = ({ open, setOpen, initialName, delAction }: DeleteTableViewProps) => {
  const [waiting, setWaiting] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={() => handleClose()} classes={{ paper: $.BR_fix }} maxWidth="sm" fullWidth>
        <Box className="dialog_header space_between" alignItems="center" pr={1}>
          <DialogTitle component={"div"} width={"90%"}>
            Save Table View
          </DialogTitle>
          <IconButton
            onClick={() => {
              handleClose();
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ display: "flex", flexFlow: "column", gap: 1 }}>
          <Alert severity="warning" sx={{ "& *": { fontFamily: "inherit" } }}>
            The table view <b>{initialName}</b> will be deleted and will not be recovered.
          </Alert>
        </DialogContent>
        <DialogActions className="dialog_footer">
          <Button
            className={$.BR_fix}
            disabled={waiting}
            onClick={async () => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <NdAsyncButton
            className={$.BR_fix}
            variant="contained"
            color="error"
            buttonComponent={Button}
            disabled={waiting}
            onClick={async () => {
              setWaiting(true);
              await delAction();
              setWaiting(false);
              setOpen(false);
            }}
          >
            Delete
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const LoadPartnerRepliesModal = ({ open, setOpen }) => {
  const { syncMail } = useContext(BcNewContext);
  return (
    <Dialog open={open} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
      <DialogTitle className="dialog-title">
        <div className="heading-m error-dialog-icon">Load Partner Replies</div>
        <IconButton
          onClick={() => {
            setOpen(false);
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography>
          You have not synced partner replies for last 24 hours. You may have received new replies.
        </Typography>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <NdButton
          variant="contained"
          onClick={() => {
            syncMail();
            setOpen(false);
          }}
        >
          Load Replies
        </NdButton>
      </DialogActions>
    </Dialog>
  );
};
